import React, { FC, useEffect, useState } from "react";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import BagIcon from "components/BagIcon";
import NcInputNumber from "components/NcInputNumber";
import { PRODUCTS, Product } from "data/data";

import Prices from "components/Prices";
import ModalViewAllReviews from "./ModalViewAllReviews";
import { apiGetItemData } from "services/MenuService";
import { useLocation, useParams } from "react-router-dom";
import NcImage from "shared/NcImage/NcImage";

import Cookies from "js-cookie";

import ProductContent from "./ProductContent";
import { useOnlineOrderSettings } from "contaxt/OnlineOrderSettingsContext";
import {
  getOnlineMenuCachedPayload,
  setOnlineMenuCacheFromResponse,
} from "utils/onlineMenuCache";
import {
  flattenOnlineMenuItems,
  findProductInMenuTree,
  unwrapMenuResponseBody,
} from "utils/onlineOrderMenuUtils";
import { ProductDetailPageSkeleton } from "shared/OnlineOrderingSkeletons";
export interface ProductDetailPageProps {
  className?: string;
}

const ProductDetailPage: FC<ProductDetailPageProps> = ({ className = "" }) => {
  const location = useLocation();
  const paramId: any = useParams();
  const id: any = paramId.id ? parseInt(paramId.id, 10) : undefined;
  const { settings, items: menuCategories } = useOnlineOrderSettings();
  const [qualitySelected, setQualitySelected] = React.useState(1);
  const [isOpenModalViewAllReviews, setIsOpenModalViewAllReviews] =
    useState(false);
  const [requiredCheck, setRequiredCheck] = useState(false);
  const [showError, setShowError] = useState(false);
  const [itemData, setItemData] = useState<Product[]>([]);
  const [menuDetailError, setMenuDetailError] = useState(false);
  const [detailLoading, setDetailLoading] = useState(
    () => id != null && !Number.isNaN(id)
  );
  const [checkedItem, setCheckedItem] = useState<Record<number, boolean>>({});
  const [modifierCheckedDatastate, setModifierCheckedDatastate] = useState<any>(
    []
  );

  useEffect(() => {
    if (id == null || Number.isNaN(id)) {
      setDetailLoading(false);
      return;
    }

    let cancelled = false;
    setDetailLoading(true);

    const state = location.state as {
      quantity?: number;
      modifierChecked?: any;
      cartItemId?: any;
      note?: string;
    } | null;

    const applyFeeCookies = (fee?: string | number, label?: string) => {
      const s = settings as {
        onlineOrderFee?: string | number;
        onlineOrderFeeLabel?: string;
      } | null;
      const f = fee ?? s?.onlineOrderFee;
      const l = label ?? s?.onlineOrderFeeLabel;
      if (f != null && f !== "") Cookies.set("orderFee", String(f));
      if (l != null && l !== "") Cookies.set("orderFeeLabel", String(l));
    };

    const applyFlattenedProducts = (flattenedItems: Product[]) => {
      Cookies.remove("formDataa");
      Cookies.remove("BillingAddress");

      const filteredData = flattenedItems
        .map((item) => ({
          ...item,
          quantity: state ? state.quantity : 1,
        }))
        .filter((item: Product) => Number(item.id) === Number(id));

      const uniqueIds = new Set<number>();
      const result = filteredData.filter((item: any) => {
        const pid = Number(item.id);
        if (!uniqueIds.has(pid)) {
          uniqueIds.add(pid);
          return true;
        }
        return false;
      });

      if (state) {
        const modifiedResult = result.map((item) => ({
          ...item,
          modifierChecked: state.modifierChecked,
          cartItemId: state.cartItemId,
          note: state.note,
        }));
        if (state.quantity != null) setQualitySelected(state.quantity);
        setItemData(modifiedResult);
      } else {
        setItemData(result);
      }
    };

    const load = async () => {
      setMenuDetailError(false);

      const feeFrom = (root: any) => root?.onlineOrderSettings;

      const tryApplyCategories = (categories: any[] | undefined, feeRoot?: any) => {
        if (!categories?.length) return false;
        let flat = flattenOnlineMenuItems(categories);
        if (!flat.some((p) => Number(p.id) === Number(id))) {
          const one = findProductInMenuTree(categories, id);
          if (!one) return false;
          const fee = feeFrom(feeRoot);
          applyFeeCookies(fee?.onlineOrderFee, fee?.onlineOrderFeeLabel);
          applyFlattenedProducts([one as Product]);
          return true;
        }
        const fee = feeFrom(feeRoot);
        applyFeeCookies(fee?.onlineOrderFee, fee?.onlineOrderFeeLabel);
        applyFlattenedProducts(flat);
        return true;
      };

      try {
        if (tryApplyCategories(menuCategories, { onlineOrderSettings: settings })) {
          return;
        }

        const cached = getOnlineMenuCachedPayload() as any;
        if (cached?.items?.length && tryApplyCategories(cached.items, cached)) {
          return;
        }

        try {
          const res = await apiGetItemData();
          const data = unwrapMenuResponseBody(res.data) as any;
          setOnlineMenuCacheFromResponse(data);
          if (tryApplyCategories(data?.items, data)) {
            return;
          }
        } catch (error) {
          console.error(error);
        }

        setItemData([]);
        setMenuDetailError(true);
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [id, menuCategories, settings, location.state]);

  useEffect(() => {
    if (itemData.length === 0) return;
    const modifierCheckedData = itemData.map((ele) => ({
      ...ele,
      modifierChecked: modifierCheckedDatastate,
    }));
    setItemData(modifierCheckedData);
  }, [modifierCheckedDatastate, location.state]);

  useEffect(() => {
    const extractedValues: any = {};
    for (const key in location.state?.modifierChecked) {
      const innerObj = location.state?.modifierChecked[key];
      for (const innerKey in innerObj) {
        extractedValues[innerKey] = innerObj[innerKey];
      }
    }
    if (location.state) {
      setCheckedItem((prevCheckedItem) => ({
        ...prevCheckedItem,
        ...extractedValues,
      }));
      setModifierCheckedDatastate(location.state.modifierChecked);
    }
  }, [location.state]);

  useEffect(() => {
    const validateRequiredModifiers = itemData.every((item) =>
      item.modifierSets?.every((modify) => {
        const selectedCount = Object.values(
          modifierCheckedDatastate[modify.id] || {}
        ).filter(Boolean).length;
        return selectedCount >= (modify.noOfRequired || 0);
      })
    );

    const validateMaximumModifiers = itemData.every((item) =>
      item.modifierSets?.every((modify) => {
        const selectedCount = Object.values(
          modifierCheckedDatastate[modify.id] || {}
        ).filter(Boolean).length;
        return !modify.maximum || selectedCount <= modify.maximum;
      })
    );

    if (itemData[0]?.modifierSets) {
      setRequiredCheck(validateRequiredModifiers && validateMaximumModifiers);
    } else {
      setRequiredCheck(true);
    }
  }, [modifierCheckedDatastate, itemData]);

  const idValid = id != null && !Number.isNaN(id);

  return (
    <>
      {idValid && detailLoading && !menuDetailError && <ProductDetailPageSkeleton />}
      {menuDetailError && itemData.length === 0 && (
        <div className="container mt-10 mb-10 text-center">
          <p className="text-lg font-semibold text-neutral-800 dark:text-neutral-100">
            This item could not be loaded.
          </p>
          <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
            Try again from the menu, or refresh the page. If you opened this link directly,
            open the restaurant home page first so the menu can load.
          </p>
        </div>
      )}
      {!detailLoading && itemData.length !== 0 && (
        <div className={`nc-ProductDetailPage ${className}`}>
          <main className="container mt-5 mb-5 lg:mt-11">
            <div className="lg:flex">
              {itemData.map((item, index) => (
                <div key={index} className="w-full lg:w-[55%] ">
                  <div className="relative">
                    <div className="aspect-w-4 aspect-h-4">
                      <NcImage
                        src={item.imageUrl}
                        containerClassName="flex aspect-w-1 aspect-h-1 w-full h-0"
                        className="w-full rounded-lg object-cover"
                      />
                    </div>

                    {/* <LikeButton className="absolute right-3 top-3 " /> */}
                  </div>
                </div>
              ))}
              <div className="w-full lg:w-[45%] pt-10 lg:pt-0 lg:pl-7 xl:pl-9 2xl:pl-10">
                {/* {renderSectionContent()} */}
                <ProductContent
                  itemData={itemData}
                  modifierCheckedDatastate={modifierCheckedDatastate}
                  qualitySelected={qualitySelected}
                  setQualitySelected={setQualitySelected}
                  id={id}
                  quantity={itemData[0].quantity}
                  note={itemData[0].note}
                  setItemData={setItemData}
                  requiredCheck={requiredCheck}
                  setShowError={setShowError}
                  checkedItem={checkedItem}
                  showError={showError}
                  setModifierCheckedDatastate={setModifierCheckedDatastate}
                />
              </div>
            </div>
            <div className="mt-12 sm:mt-16 space-y-10 sm:space-y-16">
              <div className="block xl:hidden">{/* <Policy /> */}</div>
            </div>
          </main>
          <ModalViewAllReviews
            show={isOpenModalViewAllReviews}
            onCloseModalViewAllReviews={() =>
              setIsOpenModalViewAllReviews(false)
            }
          />
        </div>
      )}
    </>
  );
};

export default ProductDetailPage;
